import {
  AbstractMyAccountUserService,
  UpdateUserDto,
  UserModel,
} from '@aionzdev/modules';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { UserApiService } from './api/user-api.service';
import { UserService } from './user.service';
import { mapUserResponseToUserModel } from '../mappers/user.mapper';

@Injectable({ providedIn: 'root' })
export class MyAccountUserService extends AbstractMyAccountUserService {
  private readonly users = inject(UserService);
  private readonly api = inject(UserApiService);

  override update(
    userData: Partial<UserModel> & { id: string },
    userAttributes?: UserModel['userAttributes'],
  ): Observable<UserModel> {
    const data: UpdateUserDto = {
      id: userData.id,
      name: userData.name ?? '',
      userName: userData.userName ?? userData.email ?? '',
      email: userData.email,
      phone: userData.phone,
      cpf: userData.cpf,
      userAttributes,
      avatar: this.isDataUrl(userData.avatar) ? undefined : userData.avatar,
      isDeleted: userData.isDeleted ?? false,
    };

    return this.users.update(data).pipe(map(mapUserResponseToUserModel));
  }

  override updateAvatar(avatar: string, userId: string): Observable<UserModel> {
    const file = this.base64ToFile(avatar, userId);

    if (!file) {
      return of({
        id: userId,
        name: '',
        userName: '',
        avatar,
        isDeleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }

    return this.api.uploadAvatar(file).pipe(
      map(({ url }) => ({
        id: userId,
        name: '',
        userName: '',
        avatar: url,
        isDeleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      }))
    );
  }

  override deactivateAccount(): Observable<void> {
    return of(void 0);
  }

  private isDataUrl(value: string | undefined): boolean {
    return value?.startsWith('data:') ?? false;
  }

  private base64ToFile(value: string, userId: string): File | null {
    const [metadata, content] = value.split(',');
    const mime = metadata?.match(/data:(.*?);base64/)?.[1];

    if (!content || !mime) {
      return null;
    }

    const bytes = Uint8Array.from(atob(content), character => character.charCodeAt(0));
    const extension = mime.split('/')[1] ?? 'png';
    return new File([bytes], `avatar-${userId}.${extension}`, { type: mime });
  }
}
